import { Box, Divider } from '@mui/material';
import { Logo } from '../../components/logo/logo';
import { GatewaySelector } from '../../features/gateways/components/gateway-selector/gateway-selector';
import { useUserProfileQuery } from '../store';
import { Gateway } from '../../types';
import { Sidebar } from '../../components/sidebar';
import UserArea from '../../components/sidebar/components/user-area';
import { gatewayMenu, menus, userAreaMenu } from './sidebar.constants';
import Scrollbars from 'react-custom-scrollbars';
import {
    HiddenTrack,
    ScrollView,
} from '../../components/scrollbars/scroll-view';
import { usePermissionChecker } from 'utils/utils';
import { useGateway } from 'hooks';
import { getGatewayAccess } from '../../features/gateways/gateway-access.utils';
import { GatewaySwitchMenuFallback } from '../../components/fallbacks/gateway-switch.fallback';

type SidebarComponentsProps = {
    collapse?: boolean;
    disableCollapse?: boolean;
    gatewaySwitching?: boolean;
};

const SidebarComponents: React.FC<SidebarComponentsProps> = ({
    collapse,
    disableCollapse,
    gatewaySwitching = false,
}) => {
    const gateway: Gateway | undefined = useGateway();
    const { data: profile } = useUserProfileQuery();
    const { hasPermission } = usePermissionChecker();
    const gatewayAccess =
        gateway && profile ? getGatewayAccess(gateway, profile.id) : undefined;
    const visibleGatewayMenuCount = gatewayMenu.filter((menu) =>
        hasPermission(
            menu.permissionKey ?? `gateway-${menu.url}-show`,
        ),
    ).length;
    const gatewaySwitchSkeletonCount = Math.max(
        1,
        (gatewayAccess?.canEnterDashboard ? 1 : 0) + visibleGatewayMenuCount,
    );

    return (
        <Sidebar.Layout
            collapse={
                collapse ||
                localStorage.getItem('sidebar_collapse') === 'true' ||
                false
            }
            disableCollapse={disableCollapse || false}>
            {({ collapse }: any) => (
                <>
                    <Box sx={{ height: '100%' }}>
                        <Scrollbars
                            renderView={ScrollView}
                            renderTrackHorizontal={HiddenTrack}
                            renderTrackVertical={HiddenTrack}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        minHeight: 54,
                                        px: collapse ? 1 : 0,
                                        pb: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderBottom: '1px solid',
                                        borderColor: 'border.light',
                                        mb: 1.5,
                                    }}
                                >
                                    <Logo size={collapse ? 'medium' : 'small'} />
                                </Box>

                                <GatewaySelector collapse={collapse} />

                                <Sidebar.MenuList>
                                    <Sidebar.MenuItem
                                        label="نمای کلی"
                                        url="/"
                                        icon={
                                            <i className="fa-light fa-grid-2"></i>
                                        }
                                    />

                                    <Divider
                                        sx={{
                                            my: 1,
                                            borderColor: 'border.light',
                                        }}
                                    />

                                    {gatewaySwitching ? (
                                        <GatewaySwitchMenuFallback
                                            expanded={collapse}
                                            itemCount={gatewaySwitchSkeletonCount}
                                        />
                                    ) : (
                                        <>
                                            {gateway?.status === 'activated' &&
                                                gatewayAccess?.canEnterDashboard && (
                                                    <Sidebar.MenuItem
                                                        label="داشبورد"
                                                        url={'/' + gateway.website}
                                                        exact
                                                        icon={
                                                            <i className="fa-light fa-gauge-high"></i>
                                                        }
                                                    />
                                                )}

                                            {gateway?.status === 'activated' &&
                                                gatewayMenu.map((menu) => (
                                                    <Sidebar.MenuItem
                                                        key={menu.url}
                                                        {...menu}
                                                        active={hasPermission(
                                                            menu.permissionKey ??
                                                                `gateway-${menu.url}-show`,
                                                        )}
                                                        url={
                                                            '/' +
                                                            gateway?.website +
                                                            '/' +
                                                            menu.url
                                                        }
                                                    />
                                                ))}

                                        </>
                                    )}

                                    {(gatewaySwitching ||
                                        gateway?.status === 'activated') && (
                                        <Divider
                                            sx={{
                                                my: 1,
                                                borderColor: 'border.light',
                                            }}
                                        />
                                    )}

                                    {menus.map((menu) => (
                                        <Sidebar.MenuItem
                                            {...menu}
                                            key={menu.url}
                                            active={menu.active}
                                            url={'/' + menu.url}
                                        />
                                    ))}
                                </Sidebar.MenuList>
                            </Box>
                        </Scrollbars>
                    </Box>
                    <UserArea
                        menu={userAreaMenu}
                        avatar={
                            profile?.avatar || profile?.avatar_color.toString()
                        }
                        avatar_color={
                            profile?.avatar_color ||
                            profile?.avatar_color.toString()
                        }
                        name={
                            profile?.name +
                            ' ' +
                            (profile?.family ? profile?.family : '')
                        }
                        account={profile?.account}
                    />
                </>
            )}
        </Sidebar.Layout>
    );
};

export default SidebarComponents;
